import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';

interface EditableTitleProps {
  title: string;
  onTitleChange: (newTitle: string) => Promise<void>;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * A reusable component for editable titles with consistent styling and behavior
 */
const EditableTitle: React.FC<EditableTitleProps> = ({ 
  title, 
  onTitleChange,
  className = '',
  size = 'lg'
}) => {
  // Track editing state to potentially show UI indicators
  const [, setIsEditing] = useState(false);
  const [originalTitle, setOriginalTitle] = useState<string>(title);
  const titleRef = useRef<HTMLHeadingElement>(null);
  
  // Update original title when prop changes
  useEffect(() => {
    setOriginalTitle(title);
    if (titleRef.current) {
      titleRef.current.textContent = title;
    }
  }, [title]);
  
  // Size-based class mapping
  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl'
  };
  
  return (
    <h1
      ref={titleRef}
      className={`font-bold px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-cm-bg-hover transition-colors ${sizeClasses[size]} ${className}`}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onFocus={() => {
        setIsEditing(true);
        setOriginalTitle(title);
      }}
      onBlur={() => {
        const newTitle = titleRef.current?.textContent?.trim() || '';
        if (newTitle !== originalTitle && newTitle) {
          onTitleChange(newTitle).catch(error => {
            console.error('Error updating title:', error);
            // Restore original title in case of error
            if (titleRef.current) titleRef.current.textContent = originalTitle;
          });
        } else if (!newTitle) {
          // Restore original title if empty
          if (titleRef.current) titleRef.current.textContent = originalTitle;
        }
        setIsEditing(false);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLHeadingElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          titleRef.current?.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          if (titleRef.current) titleRef.current.textContent = originalTitle;
          titleRef.current?.blur();
        }
      }}
    >
      {title}
    </h1>
  );
};

export default EditableTitle;
